import { useCallback, useEffect, useRef, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectBrowserNotificationsEnabled,
  setBrowserNotificationsEnabled,
} from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import {
  BROWSER_NOTIFICATION_ANALYTICS,
  BROWSER_NOTIFICATION_SETTINGS_CHANNEL,
  SENTRY_TAGS,
  TEST_BANNER_AUTO_CLOSE_MS,
  TEST_BANNER_TAG_PREFIX,
} from './constants'
import {
  getBrowserPermission,
  isBrowserNotificationSupported,
  raiseBanner,
} from './utils'

// A counter, not a timestamp: two clicks can share a millisecond and a
// duplicate tag silently suppresses the second banner. Module-level so it
// survives the Settings page remounting.
let testBannerCount = 0

export interface UseBrowserNotificationSettingsResultIProps {
  isEnabled: boolean
  isSupported: boolean
  permission: NotificationPermission | null
  showTestBanner: () => boolean
  toggleEnabled: () => Promise<void>
}

/**
 * Support detection, OS permission, and the persisted per-device preference.
 * Split from {@link useBrowserNotifications} so the Settings toggle pulls in
 * only what it needs, not the banner path.
 */
export const useBrowserNotificationSettings =
  (): UseBrowserNotificationSettingsResultIProps => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isFeatureFlagOn = useFeatureFlag(FeatureFlag.browserNotifications)
    const isEnabled = useAppSelector(selectBrowserNotificationsEnabled)
    // Reported as unsupported while the flag is off so no caller can act on a
    // capability the feature is not meant to expose yet.
    const isSupported = isFeatureFlagOn && isBrowserNotificationSupported()
    const [permission, setPermission] = useState<NotificationPermission | null>(
      null
    )

    // Read on mount, not during render: it's a live browser value. Skipped
    // entirely while the flag is off — nothing should touch the Notification
    // API for a disabled feature.
    useEffect(() => {
      if (!isFeatureFlagOn) {
        return
      }
      setPermission(getBrowserPermission())
    }, [isFeatureFlagOn])

    // Each tab has its own store, hydrated once at load, so a toggle in one
    // tab leaves every other tab still opted in — and still raising banners
    // after the operator has asked it to stop.
    const settingsChannelRef = useRef<BroadcastChannel | null>(null)

    useEffect(() => {
      if (!isFeatureFlagOn || typeof BroadcastChannel === 'undefined') {
        return
      }

      const channel = new BroadcastChannel(
        BROWSER_NOTIFICATION_SETTINGS_CHANNEL
      )
      settingsChannelRef.current = channel

      // The channel carries one thing, so the payload is the preference
      // itself. Receivers never re-broadcast, so this cannot loop.
      channel.onmessage = ({ data }: MessageEvent) => {
        if (typeof data === 'boolean') {
          dispatch(setBrowserNotificationsEnabled(data))
        }
      }

      return () => {
        channel.close()
        settingsChannelRef.current = null
      }
    }, [dispatch, isFeatureFlagOn])

    /** Applies the preference here and in every other open tab. */
    const setEnabledEverywhere = useCallback(
      (nextIsEnabled: boolean) => {
        dispatch(setBrowserNotificationsEnabled(nextIsEnabled))
        settingsChannelRef.current?.postMessage(nextIsEnabled)
      },
      [dispatch]
    )

    /**
     * MUST be called from a user gesture — Safari auto-denies a prompt raised
     * on load, Chrome suppresses it. Once denied, this resolves 'denied'
     * forever with no way to re-prompt, hence the blocked state in the UI.
     */
    const requestPermission = useCallback(async (): Promise<void> => {
      try {
        const result = await window.Notification.requestPermission()
        setPermission(result)
        setEnabledEverywhere(result === 'granted')

        trackEvent(BROWSER_NOTIFICATION_ANALYTICS.permissionResult, {
          permission_result: result,
        })
      } catch (e) {
        // Safari < 16.4 rejects the promise form. Fail soft — leave the
        // toggle off rather than breaking the Settings page.
        Sentry.captureException(e, {
          tags: { ...SENTRY_TAGS, action: 'request_permission' },
        })
      }
    }, [setEnabledEverywhere])

    const toggleEnabled = useCallback(async (): Promise<void> => {
      if (!isFeatureFlagOn) {
        return
      }

      const shouldTurnOn = !isEnabled
      trackEvent(BROWSER_NOTIFICATION_ANALYTICS.toggleEnabled, {
        is_now_on: shouldTurnOn,
      })

      if (!shouldTurnOn || !isSupported) {
        setEnabledEverywhere(false)
        return
      }

      // Already granted earlier — the browser would no-op a re-prompt.
      if (getBrowserPermission() === 'granted') {
        setPermission('granted')
        setEnabledEverywhere(true)
        return
      }

      await requestPermission()
    }, [
      isEnabled,
      isFeatureFlagOn,
      isSupported,
      requestPermission,
      setEnabledEverywhere,
    ])

    /**
     * Lets an operator verify banners reach their screen, since OS-level
     * suppression (macOS Focus, Windows Focus Assist, an app-level
     * notifications toggle) is undetectable from the page. Mirrors the audio
     * setting's "Test Alert" button.
     *
     * Skips the visibility and type gates `showIncidentBanner` applies — this
     * is an explicit user action and the Settings tab is by definition
     * visible.
     */
    const showTestBanner = useCallback((): boolean => {
      if (!isFeatureFlagOn) {
        return false
      }

      trackEvent(BROWSER_NOTIFICATION_ANALYTICS.testBanner)

      if (!isSupported || getBrowserPermission() !== 'granted') {
        return false
      }

      return raiseBanner({
        action: 'show_test_banner',
        // Unlike an incident banner, a lingering test entry is pure noise.
        autoCloseMs: TEST_BANNER_AUTO_CLOSE_MS,
        body: t('settings:rebrand_account_browser_notifications_test_body'),
        // Unique per click: a repeated tag is treated as a replacement and
        // does not re-alert, which made the button look broken.
        tag: `${TEST_BANNER_TAG_PREFIX}-${++testBannerCount}`,
        title: t('settings:rebrand_account_browser_notifications_test_title'),
      })
    }, [isFeatureFlagOn, isSupported, t])

    return {
      isEnabled,
      isSupported,
      permission,
      showTestBanner,
      toggleEnabled,
    }
  }
